import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { EnabledWallet } from '__generated__/globalTypes';
import { useSportContext } from 'contexts/sport';

import type { useAcceptedCurrencies_publicUserInfoInterface } from './__generated__/useAcceptedCurrencies.graphql';

export const MARKETPLACE_PREFERENCES_ACCEPTED_CURRENCIES =
  'accepted_currencies' as const;

export enum AcceptedCurrenciesValue {
  FIAT = 'fiat',
  CRYPTO = 'crypto',
  BOTH = 'both',

  // @deprecated Use CRYPTO instead. Kept for backward compatibility
  ETH = 'eth',
}

type UsedAcceptedCurrenciesValue =
  | typeof AcceptedCurrenciesValue.FIAT
  | typeof AcceptedCurrenciesValue.CRYPTO
  | typeof AcceptedCurrenciesValue.BOTH;

type Props = {
  user?: Nullable<useAcceptedCurrencies_publicUserInfoInterface>;
};

export const useAcceptedCurrencies = ({ user }: Props) => {
  const { sport } = useSportContext();

  const { enabledWallets, marketplacePreferences } = user?.profile || {};

  const storedAcceptedCurrencies = marketplacePreferences
    // we currently use the same preferences for all sports
    ?.find(mp => mp && mp.sport === sport)
    ?.preferences?.find(
      p => p.name === MARKETPLACE_PREFERENCES_ACCEPTED_CURRENCIES
    )?.value as AcceptedCurrenciesValue;

  const acceptedCurrencies: UsedAcceptedCurrenciesValue = useMemo(() => {
    if (!enabledWallets) {
      return AcceptedCurrenciesValue.CRYPTO;
    }
    if (enabledWallets.length === 1) {
      const walletType = enabledWallets[0];
      // Handle backward compatibility: map 'eth' to 'crypto'
      if (walletType === EnabledWallet.ETH) {
        return AcceptedCurrenciesValue.CRYPTO;
      }
      return AcceptedCurrenciesValue[walletType];
    }
    if (storedAcceptedCurrencies) {
      // Handle backward compatibility: map 'eth' to 'crypto' in stored preferences
      if (storedAcceptedCurrencies === AcceptedCurrenciesValue.ETH) {
        return AcceptedCurrenciesValue.CRYPTO;
      }
      return storedAcceptedCurrencies;
    }
    return AcceptedCurrenciesValue.BOTH;
  }, [enabledWallets, storedAcceptedCurrencies]);

  return {
    acceptedCurrencies,
    storedAcceptedCurrencies,
  };
};

useAcceptedCurrencies.fragments = {
  publicUserInfoInterface: gql`
    fragment useAcceptedCurrencies_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      profile {
        id
        enabledWallets
        marketplacePreferences(sports: [FOOTBALL, NBA, BASEBALL]) {
          sport
          preferences {
            name
            value
          }
        }
      }
    }
  ` as TypedDocumentNode<useAcceptedCurrencies_publicUserInfoInterface>,
};
